import { useState } from "react";
import { BarChart3, Calendar, Loader2, TrendingUp, Wallet, User } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useConversions } from "@/hooks/useConversions";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { DataPagination } from "@/components/shared/DataPagination";
import type { Conversion } from "@/types/api";

function fmtMoney(n: string | number) {
  return Number(n).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtValor(n: string | number, usaPontos: boolean) {
  return usaPontos ? `${Math.round(Number(n))} pts` : `R$ ${fmtMoney(n)}`;
}

function ConversionRow({ conversion, isVendedor }: { conversion: Conversion; isVendedor: boolean }) {
  const usaPontos = conversion.usa_pontos;
  return (
    <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
      <div className="space-y-0.5">
        <p className="text-sm font-medium">{fmtValor(conversion.valor_bruto, usaPontos)}</p>
        {isVendedor && (
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <User className="h-3 w-3" />
            {conversion.influenciador_email}
          </p>
        )}
        <p className="text-xs text-muted-foreground flex items-center gap-1">
          <Calendar className="h-3 w-3" />
          {new Date(conversion.created_at).toLocaleString("pt-BR")}
        </p>
      </div>
      <div className="text-right space-y-0.5">
        {parseFloat(conversion.desconto_valor) > 0 && (
          <p className="text-xs text-green-600">- {fmtValor(conversion.desconto_valor, usaPontos)} desconto</p>
        )}
        {!isVendedor && (
          <p className="text-xs text-muted-foreground">
            comissão: {fmtValor(conversion.remuneracao_valor, usaPontos)}
          </p>
        )}
      </div>
    </div>
  );
}

export default function Conversions() {
  const [page, setPage] = useState(1);
  const { data, isLoading } = useConversions(page);
  const { data: currentUser } = useCurrentUser();
  const isVendedor = currentUser?.roles?.includes("indiqr-vendedor") ?? false;

  const conversions = data?.items ?? [];

  const reaisConversions = conversions.filter((c) => !c.usa_pontos);
  const pontosConversions = conversions.filter((c) => c.usa_pontos);

  const totalVendasReais = reaisConversions.reduce((acc, c) => acc + parseFloat(c.valor_bruto), 0);
  const totalComissaoReais = reaisConversions.reduce((acc, c) => acc + parseFloat(c.remuneracao_valor), 0);
  const totalVendasPontos = pontosConversions.reduce((acc, c) => acc + parseFloat(c.valor_bruto), 0);
  const totalComissaoPontos = pontosConversions.reduce((acc, c) => acc + parseFloat(c.remuneracao_valor), 0);

  const hasBoth = reaisConversions.length > 0 && pontosConversions.length > 0;
  const hasOnlyPontos = pontosConversions.length > 0 && reaisConversions.length === 0;

  if (isLoading) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-foreground">Conversões</h1>

      <div className={`grid gap-4 ${isVendedor ? "md:grid-cols-2" : "md:grid-cols-3"}`}>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="text-xs uppercase font-semibold">Total de Vendas</CardDescription>
            <CardTitle className="text-2xl flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              {hasBoth ? (
                <span className="flex flex-col gap-0.5 text-lg">
                  <span>R$ {fmtMoney(totalVendasReais)}</span>
                  <span className="text-base">{Math.round(totalVendasPontos)} pts</span>
                </span>
              ) : hasOnlyPontos ? (
                `${Math.round(totalVendasPontos)} pts`
              ) : (
                `R$ ${fmtMoney(totalVendasReais)}`
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">{data?.total ?? 0} conversões</p>
          </CardContent>
        </Card>

        {!isVendedor && (
          <Card>
            <CardHeader className="pb-2">
              <CardDescription className="text-xs uppercase font-semibold">Total Comissão</CardDescription>
              <CardTitle className="text-2xl flex items-center gap-2">
                <Wallet className="h-5 w-5 text-primary" />
                {hasBoth ? (
                  <span className="flex flex-col gap-0.5 text-lg">
                    <span>R$ {fmtMoney(totalComissaoReais)}</span>
                    <span className="text-base">{Math.round(totalComissaoPontos)} pts</span>
                  </span>
                ) : hasOnlyPontos ? (
                  `${Math.round(totalComissaoPontos)} pts`
                ) : (
                  `R$ ${fmtMoney(totalComissaoReais)}`
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">Remuneração acumulada</p>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="text-xs uppercase font-semibold">Desconto Gerado</CardDescription>
            <CardTitle className="text-2xl flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-primary" />
              {hasOnlyPontos
                ? `${Math.round(pontosConversions.reduce((acc, c) => acc + parseFloat(c.desconto_valor), 0))} pts`
                : `R$ ${fmtMoney(reaisConversions.reduce((acc, c) => acc + parseFloat(c.desconto_valor), 0))}`}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground">Economia para clientes</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Histórico</CardTitle>
          <CardDescription>Todas as conversões registradas.</CardDescription>
        </CardHeader>
        <CardContent>
          {conversions.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-12 text-center">
              <BarChart3 className="h-10 w-10 text-muted-foreground/40 mb-3" />
              <p className="text-muted-foreground text-sm">Nenhuma conversão registrada ainda.</p>
            </div>
          ) : (
            <>
              <div className="space-y-3">
                {conversions.map((c) => (
                  <ConversionRow key={c.id} conversion={c} isVendedor={isVendedor} />
                ))}
              </div>
              <div className="mt-4">
                <DataPagination
                  page={data?.page ?? 1}
                  pages={data?.pages ?? 1}
                  onPageChange={setPage}
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
